import { LitElement, css, html } from "lit";

/**
 * Icon Switch button, based on a native checkbox.
 * Checked state corresponds to "right" option selected, unchecked to "left".
 * @class Panoramax.components.ui.IconSwitch
 * @element pnx-icon-switch
 * @extends [lit.LitElement](https://lit.dev/docs/api/LitElement/)
 * @fires Panoramax.components.ui.IconSwitch#change
 * @example
 * ```html
 * <pnx-icon-switch
 *   checked="true"
 *   name="my-switch"
 *   @change=${doSomething}
 * >
 *   <img src="left.svg" slot="icon-left" />
 *   <span slot="label-left">Left</span>
 *   <img src="right.svg" slot="icon-right" />
 *   <span slot="label-right">Right</span>
 * </pnx-icon-switch>
 * ```
 */
export default class IconSwitch extends LitElement {
	static styles = css`
		.toggle {
			cursor: pointer;
			display: flex;
			align-items: flex-start;
			justify-content: center;
		}

		.toggle-switch {
			display: inline-block;
			background: var(--widget-option-left);
			border-radius: 11px;
			width: 42px;
			min-width: 42px;
			max-width: 42px;
			height: 22px;
			min-height: 22px;
			max-height: 22px;
			position: relative;
			vertical-align: middle;
			transition: background 0.25s;
			flex: 1;
		}
		.toggle-switch:before, .toggle-switch:after {
			content: "";
		}
		.toggle-switch:before {
			display: block;
			background: linear-gradient(to bottom, #fff 0%, #eee 100%);
			border-radius: 50%;
			box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.25);
			width: 16px;
			height: 16px;
			position: absolute;
			top: 3px;
			left: 3px;
			transition: left 0.25s;
		}
		.toggle:hover .toggle-switch:before {
			background: linear-gradient(to bottom, #fff 0%, #fff 100%);
			box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.5);
		}
		.toggle-checkbox:checked + .toggle-switch {
			background: var(--widget-option-right);
		}
		.toggle-checkbox:disabled + .toggle-switch:before,
		.toggle-checkbox:disabled:hover + .toggle-switch:before {
			background: var(--grey-pale);
			box-shadow: none;
		}
		.toggle-checkbox:disabled + .toggle-switch {
			background: var(--grey);
			box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.25);
		}
		.toggle-checkbox:checked + .toggle-switch:before {
			left: 22px;
		}

		.toggle-icon {
			pointer-events: none;
			height: 22px;
			width: 22px;
			position: absolute;
			display: flex;
			justify-content: center;
			align-items: center;
			line-height: 11px;
			font-size: 11px;
			opacity: 1;
			transition: opacity 0.15s 0.1s;
		}

		.toggle-icon.icon-left {
			left: 0;
			color: var(--widget-option-left);
		}
		.toggle-icon.icon-right {
			right: 0;
			color: var(--widget-option-right);
		}

		.toggle-checkbox:checked + .toggle-switch .toggle-icon.icon-left,
		.toggle-checkbox:not(:checked) + .toggle-switch .toggle-icon.icon-right {
			opacity: 0;
			transition: opacity 0s;
		}

		.toggle-checkbox {
			position: absolute;
			visibility: hidden;
		}

		.toggle-label {
			margin-left: 5px;
			margin-right: 5px;
			position: relative;
			flex: 2;
			text-align: center;
			font-size: 0.9em;
			line-height: 22px;
		}
	`;

	/**
	 * Component properties.
	 * @memberof Panoramax.components.ui.IconSwitch#
	 * @type {Object}
	 * @property {boolean} checked True if checked
	 * @property {boolean} disabled Inactive if true
	 * @property {boolean} value The current switch value
	 * @property {string} name The switch name (also serves as ID)
	 */
	static properties = {
		checked: {type: Boolean, reflect: true},
		disabled: {type: Boolean, reflect: true},
		name: {type: String},
	};

	constructor() {
		super();
		this.checked = false;
		this.disabled = false;
		this.name = "pnx-icon-switch-1"; // Default name+ID to avoid issues
	}

	/** @private */
	_onChange(e) {
		this.checked = e.target.checked;
		/**
		 * Event for switch status change
		 * @event Panoramax.components.ui.IconSwitch#change
		 * @type {Event}
		 */
		this.dispatchEvent(new Event("change"));
	}

	/** @private */
	render() {
		return html`
			<label class="toggle" for=${this.name}>
				<span class="toggle-label">
					<slot name="label-left"></slot>
				</span>
				<input
					class="toggle-checkbox"
					type="checkbox"
					id=${this.name}
					name=${this.name}
					.checked=${this.checked}
					.disabled=${this.disabled}
					@change=${this._onChange.bind(this)}
				/>
				<div class="toggle-switch">
					<span class="toggle-icon icon-left">
						<slot name="icon-left"></slot>
					</span>
					<span class="toggle-icon icon-right">
						<slot name="icon-right"></slot>
					</span>
				</div>
				<span class="toggle-label">
					<slot name="label-right"></slot>
				</span>
			</label>
		`;
	}
}

customElements.define("pnx-icon-switch", IconSwitch);
